/**
 * Search-engine and social metadata.
 *
 * The application is a single HTML file (`public/index.html`) served for
 * every route, so per-page metadata has to be applied at runtime. This
 * module is the one place that knows what each route should say, and the
 * only place that touches `<head>`.
 *
 * Two rules:
 *
 * 1. **One indexable page.** Everything except `/` needs a session to be
 *    useful, so every other route is `noindex, follow`. A search result
 *    pointing at `/room/abc` is a dead end for the person who clicks it.
 * 2. **Static defaults, runtime corrections.** `public/index.html` ships
 *    the landing page's metadata as literal tags, so a crawler that runs
 *    no JavaScript still gets the right title, description and preview
 *    card. `applySeo` then updates those same tags in place: it never
 *    appends a second copy.
 *
 * `public/robots.txt` and `public/sitemap.xml` are generated from the same
 * facts by `scripts/generate-seo.mjs`; `seo.files.test.js` fails if the two
 * ever disagree.
 */

import { matchPath } from "react-router-dom";

import { ROUTES } from "./navigation";
import CRAWL_POLICY from "./seoCrawlPolicy.json";

/** Used when nothing better is available (SSR-less build, no origin). */
const FALLBACK_ORIGIN = CRAWL_POLICY.fallbackOrigin;

export const SITE_NAME = "I Don't Know Football";
export const SITE_TAGLINE =
  "Football explained for people who don't know football.";

export const DEFAULT_DESCRIPTION =
  "A free NFL companion that explains every play in plain English while you watch. No jargon, no betting, no account needed.";

export const OG_IMAGE_PATH = "/og-image.jpg";
export const OG_IMAGE_ALT =
  "I Don't Know Football: “2nd & 10” written out as a plain-English sentence.";

/** Titles for pages inside the product. The landing page sets its own. */
const titleFor = (page) => `${page} · ${SITE_NAME}`;

const stripTrailingSlash = (value) =>
  value.length > 1 ? value.replace(/\/+$/, "") : value;

/**
 * The canonical origin.
 *
 * `REACT_APP_SITE_URL` wins because a production build knows its own
 * domain; otherwise the live origin is correct for preview channels and
 * custom domains alike, and the literal fallback covers a non-browser
 * environment.
 */
export function siteOrigin() {
  const configured = process.env.REACT_APP_SITE_URL;
  if (configured) return stripTrailingSlash(configured.trim());

  if (typeof window !== "undefined" && window.location) {
    const { protocol, origin } = window.location;
    if (origin && /^https?:$/.test(protocol)) return stripTrailingSlash(origin);
  }

  return FALLBACK_ORIGIN;
}

/** Absolute URL for an in-app path. */
export function absoluteUrl(pathname = "/") {
  const path = pathname.startsWith("/") ? pathname : `/${pathname}`;
  return `${siteOrigin()}${path}`;
}

/**
 * Route metadata, most specific first. `matchPath` is exact, but keeping
 * the order meaningful makes the table readable next to `navigation.js`.
 *
 * `index: true` means "allowed in search results". Adding another one is a
 * deliberate act: it also has to earn a place in the sitemap.
 */
export const PAGE_SEO = Object.freeze([
  {
    path: ROUTES.ROOT,
    index: true,
    title: `${SITE_NAME}: the NFL explained in plain English`,
    description: DEFAULT_DESCRIPTION,
  },
  {
    path: ROUTES.WELCOME,
    index: false,
    title: titleFor("Start watching"),
    description:
      "Start watching as a guest (no account needed), or continue with Google to keep your progress.",
  },
  {
    path: ROUTES.HOME,
    index: false,
    title: titleFor("Home"),
    description: "What you can watch and learn right now.",
  },
  {
    path: ROUTES.GAMES,
    index: false,
    title: titleFor("Games"),
    description: "Games to follow, with kickoff times.",
  },
  {
    path: ROUTES.GAME,
    index: false,
    title: titleFor("Game"),
    description: "Get ready for a game and start a room.",
  },
  {
    path: ROUTES.ROOM,
    index: false,
    title: titleFor("Game room"),
    description: "A shared game room.",
  },
  {
    path: ROUTES.LEARN,
    index: false,
    title: titleFor("Learn football"),
    description:
      "Thirty short explainers that make a football game make sense.",
  },
  /**
   * Both player paths sit above CONCEPT deliberately. `seoForPath` takes
   * the first entry whose pattern matches, and `/learn/players` matches
   * `/learn/:conceptId` perfectly well, so a lower entry would never be
   * reached.
   */
  {
    path: ROUTES.TEAMS,
    index: false,
    title: titleFor("Teams and players"),
    description: "The clubs playing this week, and who plays for them.",
  },
  /* The addresses these lived at before they moved out of Learn. Listed
     so a crawler holding an old link gets the same answer the redirect
     gives a person, rather than a 404 title. */
  {
    path: ROUTES.LEGACY_PLAYERS,
    index: false,
    title: titleFor("Teams and players"),
    description: "The clubs playing this week, and who plays for them.",
  },
  {
    path: ROUTES.LEGACY_PLAYER,
    index: false,
    title: titleFor("Player"),
    description: "One player: their club, their position and their season.",
  },
  {
    path: ROUTES.PLAYER,
    index: false,
    title: titleFor("Player"),
    description: "One player: their club, their position and their season.",
  },
  {
    path: ROUTES.CONCEPT,
    index: false,
    title: titleFor("Learn football"),
    description: "One football idea, explained plainly.",
  },
  {
    path: ROUTES.PROFILE,
    index: false,
    title: titleFor("Your progress"),
    description: "Your Football IQ, concepts learned and games watched.",
  },
  {
    path: ROUTES.SETTINGS,
    index: false,
    title: titleFor("Settings"),
    description: "Appearance, account and data settings.",
  },
  {
    path: ROUTES.PRIVACY,
    index: false,
    title: titleFor("Privacy Policy"),
    description:
      "How I Don't Know Football handles account, progress, favourite and room data.",
  },
  {
    path: ROUTES.TERMS,
    index: false,
    title: titleFor("Terms and Conditions"),
    description:
      "Content ownership, data ownership and usage terms for I Don't Know Football.",
  },
  {
    path: ROUTES.STYLE_GUIDE,
    index: false,
    title: titleFor("Style guide"),
    description: "Internal design-system reference.",
  },
  {
    path: ROUTES.NOT_FOUND,
    index: false,
    title: titleFor("Page not found"),
    description: "That page doesn't exist.",
  },
]);

/** Static paths that belong in the sitemap, from the shared crawl policy. */
export const INDEXABLE_PATHS = Object.freeze([...CRAWL_POLICY.indexable]);

/**
 * Paths `robots.txt` keeps crawlers out of entirely.
 *
 * Everything else stays crawlable so the `noindex` tag can actually be
 * read: a `Disallow` would hide it. The listed paths are different: room
 * URLs are private invitations in an unbounded space, and the other two
 * have nothing a visitor could want.
 */
export const CRAWLER_DISALLOWED = Object.freeze([...CRAWL_POLICY.disallow]);

/** Metadata for a pathname. Unknown paths fall back to the 404 entry. */
export function seoForPath(pathname) {
  const match = PAGE_SEO.find((page) =>
    matchPath({ path: page.path, end: true }, pathname)
  );
  if (match) return match;
  return PAGE_SEO.find((page) => page.path === ROUTES.NOT_FOUND);
}

/* ------------------------------------------------------------------ head */

const isBrowser = () =>
  typeof document !== "undefined" && Boolean(document.head);

function upsertMeta(attr, key, content) {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    el.setAttribute("data-seo", "runtime");
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
  return el;
}

function upsertLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    el.setAttribute("data-seo", "runtime");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
  return el;
}

/**
 * Point every metadata tag at one page.
 *
 * Existing tags are updated rather than replaced, so the literal ones in
 * `public/index.html` never end up duplicated.
 */
export function applySeo({
  title,
  description,
  path = "/",
  index = false,
  image = OG_IMAGE_PATH,
} = {}) {
  if (!isBrowser()) return;

  const url = absoluteUrl(path);
  const imageUrl = absoluteUrl(image);

  document.title = title;

  upsertMeta("name", "description", description);
  upsertMeta(
    "name",
    "robots",
    index ? "index, follow, max-image-preview:large" : "noindex, follow"
  );
  upsertLink("canonical", url);

  upsertMeta("property", "og:site_name", SITE_NAME);
  upsertMeta("property", "og:locale", "en_US");
  upsertMeta("property", "og:type", "website");
  upsertMeta("property", "og:title", title);
  upsertMeta("property", "og:description", description);
  upsertMeta("property", "og:url", url);
  upsertMeta("property", "og:image", imageUrl);
  upsertMeta("property", "og:image:alt", OG_IMAGE_ALT);

  upsertMeta("name", "twitter:card", "summary_large_image");
  upsertMeta("name", "twitter:title", title);
  upsertMeta("name", "twitter:description", description);
  upsertMeta("name", "twitter:image", imageUrl);
  upsertMeta("name", "twitter:image:alt", OG_IMAGE_ALT);
}

/** Apply the metadata registered for a pathname. */
export function applySeoForPath(pathname) {
  const page = seoForPath(pathname);
  if (!page) return;
  applySeo({
    title: page.title,
    description: page.description,
    path: page.path.includes(":") ? pathname : page.path,
    index: page.index,
  });
}

/**
 * Add or replace a block of JSON-LD.
 *
 * Keyed by `id` so a page owns its own block and can take it away again on
 * unmount, leaving the site-wide graph in `index.html` untouched.
 */
export function applyJsonLd(id, data) {
  if (!isBrowser()) return;

  const selector = `script[type="application/ld+json"][data-seo-id="${id}"]`;
  let el = document.head.querySelector(selector);

  if (!data) {
    if (el) el.remove();
    return;
  }

  if (!el) {
    el = document.createElement("script");
    el.setAttribute("type", "application/ld+json");
    el.setAttribute("data-seo-id", id);
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

export function removeJsonLd(id) {
  applyJsonLd(id, null);
}
